import type { RefObject } from 'react';
import { LoadingSpinner, QueryErrorAlert } from '@shared/components';
import { FundingApproverField } from './FundingApproverField';
import { FundingCodeEntry } from './FundingCodeEntry';
import { InvoicePanel } from './InvoicePanel';
import { InvoiceSelectionLayout } from './InvoiceSelectionLayout';
import { InvoiceSuccessNotice } from './InvoiceSuccessNotice';
import { SoldServicesTable } from './SoldServicesTable';
import { SoldAddOnsTable } from './SoldAddOnsTable';
import { codeEntryHint } from '../utils/fundingApprovalCopy';
import { placeCodeCheck } from '../utils/fundingCodeCheck';
import { placeRejections } from '../utils/invoiceRejectionMessages';
import { isDetailsReady, panelSections, submitButtonVariant } from '../utils/invoicePanel';
import { submitDisabledReason } from '../utils/invoiceSubmitGates';
import { EVERYTHING_INVOICED_MESSAGE, isEverythingInvoiced } from '../utils/invoiceableLines';
import type { Agreement } from '../types/agreements.types';
import type { AgreementInvoicing } from '../hooks/useAgreementInvoicing';
import type { InvoiceDetailsForm } from '../hooks/useInvoiceDetailsForm';
import type { TopUpFormState } from '../hooks/useTopUpForm';
import { useSelectionTableRows } from '../hooks/useSelectionTableRows';

interface Props {
    agreement: Agreement;
    invoicing: AgreementInvoicing;
    detailsForm: InvoiceDetailsForm;
    topUpForm: TopUpFormState;
    dealerLocked: boolean;
    topUpPending: boolean;
    panelRef: RefObject<HTMLElement | null>;
}

const MODE_NOTE: Record<'single' | 'flexible', string> = {
    single: 'Single invoicing: one whole service and any optional extras.',
    flexible: 'Flexible invoicing: any combination of services, items and optional extras.',
};

/** Sold lines are fetched only while this tab is active; a failed or slow fetch
 *  never hides the Previously invoiced screen. Selection stays enabled in every status. */
export function RaiseInvoicePanel({ agreement, invoicing, detailsForm, topUpForm, dealerLocked, topUpPending, panelRef }: Props) {
    const { soldItemsQuery, balanceUnreadableReason, overBalance, flexible, invoiceSelection, candidatesQuery, candidatesGated, submission, availableBalance, outcome, stackedSummary, fundingApproval, fundingChoice, potTotalAvailable } = invoicing;
    const { data: lines, isLoading, isError, refetch } = soldItemsQuery;
    const { selection, totals, summaryLines, toggle, clear } = invoiceSelection;
    const tableRows = useSelectionTableRows(lines, selection, flexible);

    if (isLoading) {
        return <LoadingSpinner message="Loading sold lines" className="p-4" />;
    }

    if (isError || !lines) {
        return (
            <QueryErrorAlert message="Failed to load the agreement's sold lines." onRetry={() => refetch()} />
        );
    }

    const placed = placeRejections(submission.reasonsByTarget, {
        overBalance,
        codeField: overBalance && (fundingChoice.choice === 'arrears' || fundingChoice.choice === 'pot_total') && fundingApproval.pending !== null,
    });
    const codeCheckPlacement = placeCodeCheck(fundingApproval.codeCheck, placed);
    const candidateList = candidatesQuery.data ?? [];
    const disabledReason = submitDisabledReason({
        selectionEmpty: selection.length === 0,
        candidatesLoading: candidatesQuery.isLoading,
        candidatesError: candidatesQuery.isError,
        candidatesGated,
        candidatesEmpty: candidateList.length === 0,
        agreementActive: agreement.status === 'active',
        balanceUnreadableReason,
        overBalance,
        fundingChoice: fundingChoice.choice,
        recordedTopUpAmount: topUpForm.recordedAmount,
        topUpPosting: invoicing.topUpPosting,
        authorisationReason: fundingApproval.blockingReason,
        topUpPending,
        isPending: submission.isPending,
    });

    const successNotice = submission.lastSubmittedNumber && !invoicing.canBrowseInvoices ? (
        <InvoiceSuccessNotice invoiceNumber={submission.lastSubmittedNumber} onDismiss={submission.dismissNotice} />
    ) : null;

    return (
        <div className="@container space-y-4">
            <p className="text-sm text-mp-muted">
                {MODE_NOTE[flexible ? 'flexible' : 'single']}
            </p>

            {isEverythingInvoiced(lines) && (
                <p role="status" className="text-sm text-mp-muted">
                    {EVERYTHING_INVOICED_MESSAGE}
                </p>
            )}

            <InvoiceSelectionLayout
                panelLabel="Raise an invoice"
                panelRef={panelRef}
                panel={
                    <InvoicePanel
                        sections={panelSections({
                            agreementActive: agreement.status === 'active',
                            hasSuccessNotice: successNotice !== null,
                            overBalance,
                            detailsReady: isDetailsReady({
                                selectionEmpty: selection.length === 0,
                                overBalance,
                                fundingChoice: fundingChoice.choice,
                                recordedTopUpAmount: topUpForm.recordedAmount,
                                authorisationReason: fundingApproval.blockingReason,
                                hasSubmitReasons: placed.submit.length > 0,
                            }),
                        })}
                        successNotice={successNotice}
                        dealer={{
                            control: detailsForm.control,
                            candidates: candidateList,
                            candidatesLoading: candidatesQuery.isLoading,
                            candidatesError: candidatesQuery.isError,
                            candidatesGated,
                            locked: dealerLocked,
                            busy: topUpForm.isSubmitting,
                        }}
                        selection={{
                            totals,
                            summaryLines,
                            lineMessages: submission.lineReasons,
                            onClear: clear,
                            availableBalance,
                            outcome,
                            locked: submission.isPending,
                        }}
                        overBalance={{
                            figures: fundingApproval.figures,
                            fundingChoice,
                            potTotalAvailable,
                            topUpForm,
                            motorplan: invoicing.motorplan,
                            topUpPosting: invoicing.topUpPosting,
                            locked: submission.isPending,
                            reasons: codeCheckPlacement.overBalance,
                            authorisation: {
                                canAuthorise: fundingApproval.mode === 'self',
                                confirmed: fundingApproval.confirmed,
                                onConfirmedChange: fundingApproval.setConfirmed,
                                authorisationReason: fundingApproval.blockingReason,
                                emailOpen: fundingApproval.emailOpen,
                                notice: fundingApproval.notice,
                                emailSlot: <FundingApproverField form={fundingApproval.approverForm} />,
                                codeSlot: fundingApproval.pending && (
                                    <FundingCodeEntry
                                        code={fundingApproval.code}
                                        onCodeChange={fundingApproval.setCode}
                                        hint={codeEntryHint(fundingApproval.pending, fundingApproval.resentAt)}
                                        error={codeCheckPlacement.codeError}
                                        onResend={() => void fundingApproval.resend()}
                                        onSendToSomeoneElse={fundingApproval.reopenEmail}
                                        sending={fundingApproval.sending}
                                        checkStatus={fundingApproval.codeCheck.status}
                                        onCheck={() => void fundingApproval.codeCheck.check()}
                                    />
                                ),
                            },
                        }}
                        details={{
                            submitReasons: placed.submit,
                            form: detailsForm,
                            disabledReason,
                            submitVariant: submitButtonVariant(fundingChoice.choice, overBalance),
                        }}
                    />
                }
                services={
                    <SoldServicesTable
                        rows={tableRows.services}
                        emptyMessage={tableRows.emptyMessages.services}
                        flexible={flexible}
                        selectedKeys={tableRows.selectedKeys}
                        onToggle={toggle}
                        locked={submission.isPending}
                        summary={stackedSummary ?? undefined}
                    />
                }
                addOns={
                    <SoldAddOnsTable
                        rows={tableRows.addOns}
                        emptyMessage={tableRows.emptyMessages.addOns}
                        onToggle={toggle}
                        locked={submission.isPending}
                    />
                }
            />
        </div>
    );
}
